import { motion } from "framer-motion";
import { ASSETS } from "@/lib/brand";
import BlancheCounter from "@/components/site/BlancheCounter";

const wineCommon = {
  initial: { opacity: 0, y: 50 },
  whileInView: { opacity: 1, y: 0 },
  viewport: { once: true, margin: "-80px" },
  transition: { duration: 0.9, ease: "easeOut" },
};

function WineFacts({ items, accent }) {
  return (
    <div className="space-y-5">
      {items.map((it) => (
        <div key={it.label} className="grid grid-cols-1 gap-1.5 sm:grid-cols-12 sm:gap-4 py-3 border-b border-[#EAEAEA]">
          <div className="sm:col-span-4">
            <span className="overline text-[10px] sm:text-[9.5px]" style={{ color: accent }}>
              {it.label}
            </span>
          </div>
          <div className="sm:col-span-8">
            <p className="text-[15.5px] leading-[1.75] text-[#2C2C2C] font-light">
              {it.value}
            </p>
          </div>
        </div>
      ))}
    </div>
  );
}

export function TerraArsa() {
  const facts = [
    {
      label: "Concetto",
      value:
        "Metafora del nostro entroterra. Le radici affondano nei suoli calcareo-argillosi degli Iblei: il sole sfida la vite, trasformando siccità in eleganza.",
    },
    {
      label: "Degustazione",
      value:
        "Rubino profondo. Bouquet di frutti rossi maturi, marasca, spezie mediterranee, cacao, tocco balsamico. Tannini vellutati, persistenza interminabile.",
    },
    {
      label: "Abbinamenti",
      value:
        "Brasati, filetti d'alta scuola, primi con ragù di cinghiale, Ragusano DOP e formaggi stagionati d'élite.",
    },
    {
      label: "Scheda Tecnica",
      value:
        "Nero d'Avola 100% · DOC Sicilia · Valle del Fiume Dirillo · Vendemmia: terza settimana di settembre · Prolungata macerazione sulle bucce, fermentazione in acciaio a temperatura controllata · Affinamento in acciaio · 13% vol · Servizio 16–18 °C.",
    },
  ];

  return (
    <section
      id="terra-arsa"
      data-testid="terra-arsa-section"
      className="relative bg-white overflow-hidden"
    >
      <div className="max-w-[1480px] mx-auto px-6 sm:px-12 lg:px-20 py-24 sm:py-32 lg:py-40">
        <div className="grid grid-cols-1 lg:grid-cols-12 gap-12 lg:gap-20 items-center">
          {/* Image */}
          <motion.div {...wineCommon} className="lg:col-span-7 order-2 lg:order-1 relative">
            <div
              className="relative zoom-frame rounded-[32px] overflow-hidden"
              style={{ boxShadow: "0 0 0 1px rgba(138,74,43,0.4), 0 0 46px rgba(138,74,43,0.24), 0 24px 60px rgba(10,10,10,0.18)" }}
            >
              <img
                src="/img/terra-arsa-scene.jpg"
                alt="Terra Arsa — Nero d'Avola DOC Sicilia tra terra arsa e rocce calcaree iblee al tramonto"
                className="w-full h-[420px] sm:h-[680px] lg:h-[820px] object-cover"
                data-testid="terra-arsa-image"
              />
              <div
                aria-hidden
                className="absolute inset-0 pointer-events-none"
                style={{ boxShadow: "inset 0 0 90px rgba(10,10,10,0.3)", background: "radial-gradient(ellipse at center, transparent 60%, rgba(10,10,10,0.18) 100%)" }}
              />
            </div>
          </motion.div>

          {/* Text */}
          <motion.div
            {...wineCommon}
            transition={{ ...wineCommon.transition, delay: 0.15 }}
            className="lg:col-span-5 order-1 lg:order-2"
          >
            <div className="flex items-center gap-6 mb-6">
              <span className="section-num text-3xl">04</span>
              <div className="h-px w-12 bg-[#63171B]" />
              <span className="overline text-[10px]" style={{ color: "#63171B" }}>
                Nero d'Avola DOC Sicilia · Vintage 2025
              </span>
            </div>

            <h2
              className="font-serif-display leading-[0.92] text-[#0A0A0A]"
              style={{ fontSize: "clamp(2.8rem, 7vw, 6rem)" }}
            >
              Terra
              <br />
              <span className="italic text-[#63171B]">Arsa</span>
            </h2>

            <p className="mt-8 text-[16px] leading-[1.85] text-[#2C2C2C] font-light max-w-md">
              Un rosso potente che trasforma la siccità degli Iblei in pura
              eleganza. Colore, struttura, sostanza.
            </p>

            <div className="mt-10">
              <WineFacts items={facts} accent="#63171B" />
            </div>
          </motion.div>
        </div>
      </div>
    </section>
  );
}

export function Scilla() {
  const facts = [
    {
      label: "Concetto",
      value:
        "Se Terra Arsa è la terra, Scilla è il mare. Brezze marine che risalgono la costa ionica, mineralità e salinità uniche.",
    },
    {
      label: "Degustazione",
      value:
        "Giallo paglierino cristallino. Naso di zagara, fiori bianchi, scorza d'agrumi, erbe aromatiche. Sapidità marina, freschezza verticale.",
    },
    {
      label: "Abbinamenti",
      value:
        "Crudi di pesce, tartare di tonno o ricciola, scampi, risotti ai frutti di mare, aperitivo nei luxury hotel.",
    },
    {
      label: "Scheda Tecnica",
      value:
        "Inzolia 100% · IGP Terre Siciliane · Valle del Fiume Dirillo · Vendemmia: prima decade di settembre · Pressatura soffice e fermentazione in acciaio a temperatura controllata · Affinamento in vasche di acciaio inox · 12,5% vol · Servizio 12 °C.",
    },
  ];

  return (
    <section
      id="scilla"
      data-testid="scilla-section"
      className="relative overflow-hidden"
      style={{ background: "#FDFBF7" }}
    >
      <div className="max-w-[1480px] mx-auto px-6 sm:px-12 lg:px-20 py-24 sm:py-32 lg:py-40">
        <div className="grid grid-cols-1 lg:grid-cols-12 gap-12 lg:gap-20 items-center">
          {/* Text (left) */}
          <motion.div {...wineCommon} className="lg:col-span-5 order-1">
            <div className="flex items-center gap-6 mb-6">
              <span className="section-num text-3xl" style={{ color: "#C4A671" }}>
                05
              </span>
              <div className="h-px w-12 bg-[#C4A671]" />
              <span className="overline text-[10px]">Inzolia Terre Siciliane I.G.T. · Vintage 2025</span>
            </div>

            <h2
              className="font-serif-display leading-[0.92] text-[#0A0A0A]"
              style={{ fontSize: "clamp(2.8rem, 7vw, 6rem)" }}
            >
              <span className="italic">Scilla</span>
              <br />
              <span className="font-light text-[#7B6A48]">il mare</span>
            </h2>

            <p className="mt-8 text-[16px] leading-[1.85] text-[#2C2C2C] font-light max-w-md">
              Il mito che incontra il mare. Una sapidità tagliente, una
              freschezza tesa e verticale. L'eleganza del bianco mediterraneo.
            </p>

            <div className="mt-10">
              <WineFacts items={facts} accent="#7B6A48" />
            </div>
          </motion.div>

          {/* Image (right) */}
          <motion.div
            {...wineCommon}
            transition={{ ...wineCommon.transition, delay: 0.15 }}
            className="lg:col-span-7 order-2 relative"
          >
            <div
              className="relative zoom-frame rounded-[32px] overflow-hidden"
              style={{ boxShadow: "0 0 0 1px rgba(74,127,155,0.4), 0 0 46px rgba(74,127,155,0.24), 0 24px 60px rgba(10,10,10,0.16)" }}
            >
              <img
                src="/img/scilla-scene.jpg"
                alt="Scilla — Inzolia tra scogliere calcaree e mare cristallino blu salmastro"
                className="w-full h-[420px] sm:h-[680px] lg:h-[820px] object-cover"
                data-testid="scilla-image"
              />
              <div
                aria-hidden
                className="absolute inset-0 pointer-events-none"
                style={{ boxShadow: "inset 0 0 90px rgba(10,10,10,0.22)", background: "radial-gradient(ellipse at center, transparent 60%, rgba(10,10,10,0.14) 100%)" }}
              />
            </div>
          </motion.div>
        </div>
      </div>
    </section>
  );
}

export function Claire() {
  const facts = [
    {
      label: "Concetto",
      value:
        "Dall'unione tra la freschezza vibrante del Frappato e la struttura calcareo-argillosa del Nero d'Avola nasce Claire. Un rosato d'autore che rifiuta i compromessi della banalità: tensione minerale, sapidità tagliente e un vivo rosa salmone con caldi riflessi rame e corallo.",
    },
    {
      label: "Degustazione",
      value:
        "Un'elegante e viva sfumatura rosa salmone, impreziosita da caldi riflessi rame e corallo messi in risalto dalla trasparenza della Borgognotta. Naso teso di pompelmo rosa, scorza d'arancia e piccoli frutti di bosco. Al palato è elettrico, sorretto da un'acidità vibrante e un finale sapido, lungo e gastronomico.",
    },
    {
      label: "Abbinamenti",
      value:
        "Crudi di mare, crostacei, tartare di tonno rossastro, cucina giapponese d'autore, ceviche e piatti estivi di alta scuola.",
    },
    {
      label: "Scheda Tecnica",
      value:
        "Frappato 50%, Nero d'Avola 50% · IGT Terre Siciliane · Valle del Fiume Dirillo · Vendemmia: prima settimana di settembre · Vinificazione in rosa con brevissimo contatto e pressatura soffice, fermentazione in acciaio inox a temperatura controllata · 12,5% vol · Servizio 10–12 °C.",
    },
  ];

  return (
    <section
      id="claire"
      data-testid="claire-section"
      className="relative overflow-hidden"
      style={{ background: "#FDF6F4" }}
    >
      <div className="max-w-[1480px] mx-auto px-6 sm:px-12 lg:px-20 py-24 sm:py-32 lg:py-40">
        <div className="grid grid-cols-1 lg:grid-cols-12 gap-12 lg:gap-20 items-center">
          {/* Image */}
          <motion.div {...wineCommon} className="lg:col-span-7 order-2 lg:order-1 relative">
            <div
              className="relative zoom-frame rounded-[32px] overflow-hidden"
              style={{ boxShadow: "0 0 0 1px rgba(183,110,121,0.45), 0 0 46px rgba(232,180,192,0.32), 0 24px 60px rgba(10,10,10,0.14)" }}
            >
              <img
                src="/img/claire-scene.jpg"
                alt="Claire — Rosato di Frappato e Nero d'Avola dal vivo colore rosa salmone con riflessi rame e corallo, sul mare mediterraneo"
                className="w-full h-[420px] sm:h-[680px] lg:h-[820px] object-cover"
                data-testid="claire-image"
              />
              <div
                aria-hidden
                className="absolute inset-0 pointer-events-none"
                style={{ boxShadow: "inset 0 0 90px rgba(10,10,10,0.2)", background: "radial-gradient(ellipse at center, transparent 60%, rgba(10,10,10,0.12) 100%)" }}
              />
            </div>
          </motion.div>

          {/* Text */}
          <motion.div
            {...wineCommon}
            transition={{ ...wineCommon.transition, delay: 0.15 }}
            className="lg:col-span-5 order-1 lg:order-2"
          >
            <div className="flex items-center gap-6 mb-6">
              <span className="section-num text-3xl" style={{ color: "#B76E79" }}>
                07
              </span>
              <div className="h-px w-12" style={{ background: "#B76E79" }} />
              <span className="overline text-[10px]" style={{ color: "#B76E79" }}>
                Rosato di Frappato &amp; Nero d&apos;Avola · Vintage 2025 · Micro-lotto
              </span>
            </div>

            <h2
              className="font-serif-display leading-[0.92] text-[#0A0A0A]"
              style={{ fontSize: "clamp(2.8rem, 7vw, 6rem)" }}
            >
              <span className="italic">Claire</span>
              <br />
              <span className="font-light" style={{ color: "#B76E79" }}>
                l&apos;altra faccia
                <br />
                della luce.
              </span>
            </h2>

            <p className="mt-8 text-[16px] leading-[1.85] text-[#2C2C2C] font-light max-w-md">
              Un rosato d&apos;autore in micro-lotto: tensione minerale, sapidità
              tagliente e un vivo rosa salmone con caldi riflessi rame e corallo.
            </p>

            <div className="mt-10">
              <WineFacts items={facts} accent="#B76E79" />
            </div>
          </motion.div>
        </div>
      </div>
    </section>
  );
}

export function Blanche() {
  const facts = [
    {
      label: "Concetto",
      value:
        "Sfidare l'ovvio. Prendiamo l'uva rossa più identitaria del nostro territorio — il Frappato — e la priviamo della sua veste cromatica attraverso una millimetrica vinificazione in bianco. Da questa metamorfosi, legata in un blend sartoriale a Grillo, Catarratto e Zibibbo, nasce Blanche: il cuore degli Iblei unito a un mosaico d'eccellenza siciliano.",
    },
    {
      label: "Degustazione",
      value:
        "Cristallino e luminoso. Un naso teso di zagara, agrumi e complesse sfumature aromatiche. Al palato è una bomba di freschezza, sapidità e persistenza.",
    },
    {
      label: "Esclusività",
      value:
        "1.333 bottiglie trasparenti numerate a mano, sigillate con cartellino d'oro. Ogni cartellino riporta la numerazione estratta dal registro (es. #0058 · #0236 · #1023). Poche casse disponibili per singolo locale.",
    },
    {
      label: "Scheda Tecnica",
      value:
        "Catarratto 25% · Grillo 25% · Zibibbo 25% · Frappato 25% (vinificato in bianco) · IGP Terre Siciliane · Valle del Fiume Dirillo · Vendemmia: inizio settembre, raccolta scalare per singolo vitigno · Fermentazioni separate in acciaio e assemblaggio delle quattro masse · 12,5–13% vol · Servizio 10–12 °C.",
    },
  ];

  return (
    <section
      id="blanche"
      data-testid="blanche-section"
      className="relative bg-[#0A0A0A] text-[#F2EFE8] overflow-hidden"
    >
      <div className="absolute inset-0 grain opacity-25" aria-hidden />

      <div className="relative max-w-[1480px] mx-auto px-6 sm:px-12 lg:px-20 py-24 sm:py-32 lg:py-40">
        <div className="grid grid-cols-1 lg:grid-cols-12 gap-12 lg:gap-20 items-center">
          {/* Image */}
          <motion.div {...wineCommon} className="lg:col-span-7 order-2 lg:order-1">
            <div className="relative">
              <div
                className="relative zoom-frame rounded-[32px] overflow-hidden"
                style={{ boxShadow: "0 0 0 1px rgba(196,166,113,0.5), 0 0 52px rgba(196,166,113,0.3), 0 24px 60px rgba(0,0,0,0.4)" }}
              >
                <img
                  src="/img/blanche-scene.jpg"
                  alt="Blanche — Blanc de Noirs & Blend d'Autore, fotografia editoriale su marmo scuro, 1.333 bottiglie numerate"
                  className="w-full h-[420px] sm:h-[680px] lg:h-[820px] object-cover"
                  data-testid="blanche-image"
                />
                <div
                  aria-hidden
                  className="absolute inset-0 pointer-events-none"
                  style={{ boxShadow: "inset 0 0 110px rgba(0,0,0,0.45)", background: "radial-gradient(ellipse at center, transparent 55%, rgba(0,0,0,0.3) 100%)" }}
                />
              </div>

              {/* Sigillo Vintage — ancorato all'immagine */}
              <div
                className="mt-3 border px-4 py-2 text-center sm:text-left sm:mt-0 sm:absolute sm:bottom-6 sm:right-6"
                style={{
                  background: "rgba(10,10,10,0.85)",
                  borderColor: "#C4A671",
                }}
                data-testid="blanche-badge"
              >
                <p
                  className="overline text-[9px]"
                  style={{ color: "#C4A671", letterSpacing: "0.28em" }}
                >
                  Limited Edition · 1333 · Vintage 2025
                </p>
              </div>
            </div>

            {/* Banner Registro Allocazioni — sotto l'immagine */}
            <div className="mt-6">
              <BlancheCounter />
            </div>
          </motion.div>

          {/* Text */}
          <motion.div
            {...wineCommon}
            transition={{ ...wineCommon.transition, delay: 0.15 }}
            className="lg:col-span-5 order-1 lg:order-2"
          >
            <div className="flex items-center gap-6 mb-6">
              <span
                className="section-num text-3xl"
                style={{ color: "#C4A671" }}
              >
                06
              </span>
              <div className="h-px w-12" style={{ background: "#C4A671" }} />
              <span
                className="overline text-[10px]"
                style={{ color: "#C4A671" }}
              >
                Blanche — Blanc de Noirs &amp; Blend d&apos;Autore
              </span>
            </div>

            <h2
              className="font-serif-display leading-[0.92] text-[#F2EFE8]"
              style={{ fontSize: "clamp(2.6rem, 6.5vw, 5.6rem)" }}
            >
              La metamorfosi
              <br />
              <span className="italic font-light" style={{ color: "#C4A671" }}>
                della luce.
              </span>
            </h2>

            <p
              className="mt-8 text-[16px] leading-[1.9] font-light max-w-md"
              style={{ color: "#C9C3B5" }}
            >
              Dall&apos;estro di{" "}
              <em className="font-serif-display not-italic text-[#F2EFE8]">
                Claudio Pucci
              </em>
              , una provocazione enologica racchiusa in sole{" "}
              <span className="font-serif-display italic text-[#F2EFE8]">
                1.333 bottiglie trasparenti
              </span>
              , numerate progressivamente a mano. Un&apos;allocazione d&apos;élite
              riservata esclusivamente all&apos;alta ristorazione e al Sommelier
              Club.
            </p>

            <div className="mt-10 space-y-5">
              {facts.map((it) => (
                <div
                  key={it.label}
                  className="grid grid-cols-1 gap-1.5 sm:grid-cols-12 sm:gap-4 py-3 border-b"
                  style={{ borderColor: "#1f1f1f" }}
                >
                  <div className="sm:col-span-4">
                    <span
                      className="overline text-[10px] sm:text-[9.5px]"
                      style={{ color: "#C4A671" }}
                    >
                      {it.label}
                    </span>
                  </div>
                  <div className="sm:col-span-8">
                    <p
                      className="text-[15.5px] leading-[1.75] font-light"
                      style={{ color: "#C9C3B5" }}
                    >
                      {it.value}
                    </p>
                  </div>
                </div>
              ))}
            </div>
          </motion.div>
        </div>
      </div>
    </section>
  );
}
